import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { splitAddons, toggleAddon, canAddWithAddons, orderSelection, type Addon } from '@/lib/addonChoice';

interface SheetItem {
  id: string;
  name: string;
  price: number;
  addons?: Addon[] | null;
}

type Translate = (key: string, opts?: { defaultValue?: string }) => string;

/**
 * The fan's add-on sheet for one menu item. Two sections: the REQUIRED choice
 * (radio, exactly one, e.g. the kind of tea; Add stays disabled until it is
 * made) and the optional extras (checkboxes, any number, e.g. milk, sugar).
 * An item without a required section shows only the extras, as before. The
 * checkout applies the same rule (addon_choice_required).
 */
export function AddonsSheet({ item, t, primaryColor, onAdd, onClose, initialSelection }: {
  item: SheetItem;
  t: Translate;
  primaryColor: string;
  /** The picked add-ons in menu order (the required choice first). */
  onAdd: (selection: Addon[]) => void;
  onClose: () => void;
  /** Prefill, e.g. the picks of the cart line whose "+" opened the sheet. */
  initialSelection?: Addon[];
}) {
  const [selected, setSelected] = useState<Addon[]>(initialSelection ?? []);
  const { choice, extras } = splitAddons(item);
  const isPicked = (addon: Addon) => selected.some((a) => a.name === addon.name);
  const total = item.price + selected.reduce((s, a) => s + a.price, 0);

  const row = (addon: Addon, idx: number) => (
    <div
      key={`${addon.choice ? 'c' : 'x'}-${idx}`}
      role={addon.choice ? 'radio' : undefined}
      aria-checked={addon.choice ? isPicked(addon) : undefined}
      className={`flex items-center justify-between gap-3 p-3 rounded-xl border bg-neutral-50 ${addon.choice && isPicked(addon) ? 'border-neutral-900' : 'border-neutral-100'}`}
      onClick={() => setSelected(toggleAddon(item, selected, addon))}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {addon.choice ? (
          <span aria-hidden className="h-4 w-4 flex-shrink-0 rounded-full border-2 border-neutral-900 grid place-items-center">
            {isPicked(addon) && <span className="h-2 w-2 rounded-full bg-neutral-900" />}
          </span>
        ) : (
          <Checkbox checked={isPicked(addon)} />
        )}
        <span className="font-medium text-neutral-900 break-words">{t(`menu.addon.${addon.name}`, { defaultValue: addon.name })}</span>
      </div>
      {addon.price > 0 && <span className="text-sm font-bold text-neutral-500 flex-shrink-0 tabular-nums whitespace-nowrap">+€{addon.price.toFixed(2)}</span>}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-scrim-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl animate-sheet-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="min-w-0 flex-1 pr-2">
              <h3 className="text-xl font-bold text-neutral-900 leading-snug break-words">{t(`menu.item.name.${item.id}`, { defaultValue: item.name })}</h3>
              {choice.length === 0 && <p className="text-sm text-neutral-500">{t('ordering.chooseAddons')}</p>}
            </div>
            <button aria-label="Close" onClick={onClose} className="p-2 text-neutral-400 flex-shrink-0 -mr-2 -mt-1"><X className="w-6 h-6" /></button>
          </div>
          <div className="space-y-3 mb-8 max-h-[40vh] overflow-y-auto">
            {choice.length > 0 && (
              <div role="radiogroup" aria-label={t('ordering.chooseOne')} className="space-y-3">
                <p className="text-xs font-bold uppercase tracking-wide text-neutral-500">{t('ordering.chooseOne')}</p>
                {choice.map(row)}
              </div>
            )}
            {choice.length > 0 && extras.length > 0 && (
              <p className="pt-2 text-xs font-bold uppercase tracking-wide text-neutral-500">{t('ordering.optionalExtras')}</p>
            )}
            {extras.map(row)}
          </div>
          <Button
            className="w-full h-14 text-base font-bold rounded-xl"
            style={{ backgroundColor: primaryColor }}
            disabled={!canAddWithAddons(item, selected)}
            onClick={() => onAdd(orderSelection(item, selected))}
          >
            {t('ordering.addItem')} - €{total.toFixed(2)}
          </Button>
        </div>
      </div>
    </div>
  );
}
